import IconClose from '@apollo/icons/default/IconClose.svg';
import IconError from '@apollo/icons/default/IconError.svg';
import IconWarning from '@apollo/icons/default/IconWarning.svg';
import { Badge, IconButton } from '@apollo/orbit';
import classnames from 'classnames';
import _ from 'lodash';
import * as monacoEditor from 'monaco-editor';
import React, { useMemo } from 'react';
import { useApolloGraphqlDiagnostics } from 'register-apollo-graphql-language';
import { UNATTRIBUTED_DIAGNOSTICS_URI } from 'register-apollo-graphql-language/src/ApolloGraphqlDiagnosticsProvider';

import { SensiblyBreakCamelCase } from 'src/app/graph/explorerPage/components/sensiblyBreakCamelCase/SensiblyBreakCamelCase';
import { format } from 'src/lib/format';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

export const UnattributedDiagnosticsDrawer = ({
  onClose,
  className,
}: {
  onClose: () => void;
  className?: string;
}) => {
  const { [UNATTRIBUTED_DIAGNOSTICS_URI]: unattributedDiagnostics = [] } =
    useApolloGraphqlDiagnostics();

  // Diagnostics will either be all errors or all hints
  const diagnosticsAreErrors =
    unattributedDiagnostics[0]?.severity === monacoEditor.MarkerSeverity.Error;
  const diagnosticsByCode = useMemo(
    () =>
      _.groupBy(unattributedDiagnostics, (diagnostic) => {
        if (!diagnostic.code) return 'UNKNOWN';
        return typeof diagnostic.code === 'string'
          ? diagnostic.code
          : diagnostic.code.value;
      }),
    [unattributedDiagnostics],
  );

  return (
    <div
      className={classnames(
        'z-30 flex h-full w-[26rem] flex-col border-l border-l-silver-dark bg-white 4xl:w-[37rem]',
        className,
      )}
    >
      <div className="flex items-center justify-between border-b border-b-silver-dark px-1 py-2">
        <h3 className="pl-3 text-base font-semibold text-black">
          Unattributed {diagnosticsAreErrors ? 'Errors' : 'Hints'}
        </h3>
        <div className="flex items-center space-x-2">
          <IconButton
            variant="hidden"
            aria-label="Close drawer"
            icon={<IconClose />}
            onClick={onClose}
          />
        </div>
      </div>
      <div
        className={classnames(
          'flex min-h-0 flex-1 flex-col overflow-y-auto',
          !isMinLengthArray(1, unattributedDiagnostics)
            ? 'items-center justify-center'
            : 'px-6',
        )}
      >
        <div className="my-6 space-y-3">
          {Object.entries(diagnosticsByCode).map(([code, diagnostics]) => {
            return (
              <div key={code} className="w-full rounded border border-silver">
                <div className="flex justify-between p-4">
                  <div className="flex w-full">
                    {diagnosticsAreErrors ? (
                      <IconError className="mr-2 mt-1 text-icon-error" />
                    ) : (
                      <IconWarning className="mr-2 mt-1 text-icon-warning" />
                    )}

                    <div className="flex w-0 flex-1 flex-col space-y-1">
                      <div className="flex items-center space-x-2">
                        <h3
                          className="truncate text-base font-semibold text-secondary"
                          title={code}
                        >
                          {code}
                        </h3>
                        <Badge className="ml-1">
                          {diagnostics.length}{' '}
                          {format.pluralize(
                            diagnostics.length,
                            diagnosticsAreErrors ? 'Error' : 'Hint',
                          )}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="border-t border-silver">
                  <div className="divide-y divide-silver">
                    {diagnostics.map((diagnostic) => (
                      <p
                        key={diagnostic.message}
                        className="p-4 text-secondary [&>*]:inline"
                      >
                        <SensiblyBreakCamelCase
                          camelCasedString={diagnostic.message}
                        />
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
